import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Activity, 
  TrendingUp, 
  TrendingDown, 
  Building2, 
  CheckCircle,
  AlertTriangle,
  DollarSign,
  Brain,
  Sparkles,
  RefreshCw
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';

interface DashboardHeroProps {
  monthlyRevenue: number;
  projectStats: { total: number; inProgress: number; delayed: number; completed: number };
  completionRate: number;
  alertsCount: number;
}

interface HealthMetrics {
  profitMargin: number;
  taskVelocity: number;
  proposalConversion: number;
  projectProgress: number;
}

interface DiagnosisResult {
  diagnosis: string;
  action: string;
}

const getScoreGrade = (score: number): { grade: string; color: string; label: string } => {
  if (score >= 85) return { grade: 'A', color: 'text-emerald-500', label: 'Excelente' };
  if (score >= 70) return { grade: 'B', color: 'text-green-500', label: 'Bom' };
  if (score >= 55) return { grade: 'C', color: 'text-yellow-500', label: 'Regular' };
  if (score >= 40) return { grade: 'D', color: 'text-orange-500', label: 'Atenção' };
  return { grade: 'F', color: 'text-red-500', label: 'Crítico' };
};

const getScoreRingColor = (score: number): string => {
  if (score >= 70) return 'stroke-emerald-500';
  if (score >= 55) return 'stroke-yellow-500';
  if (score >= 40) return 'stroke-orange-500';
  return 'stroke-red-500';
};

export const DashboardHero = ({ 
  monthlyRevenue, 
  projectStats, 
  completionRate, 
  alertsCount 
}: DashboardHeroProps) => {
  const { user } = useAuth();
  const [score, setScore] = useState(0);
  const [metrics, setMetrics] = useState<HealthMetrics>({
    profitMargin: 0,
    taskVelocity: 0,
    proposalConversion: 0,
    projectProgress: 0
  });
  const [diagnosis, setDiagnosis] = useState<DiagnosisResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [diagnosisLoading, setDiagnosisLoading] = useState(false);

  const calculateHealthScore = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;
      const startDate = `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`;
      const endDate = new Date(currentYear, currentMonth, 0).toISOString().split('T')[0];

      const { data: receitas } = await supabase
        .from('financeiro_usuario')
        .select('valor')
        .eq('tipo', 'recebimento')
        .gte('data', startDate)
        .lte('data', endDate);

      const { data: despesas } = await supabase
        .from('financeiro_usuario')
        .select('valor')
        .eq('tipo', 'despesa')
        .gte('data', startDate)
        .lte('data', endDate);

      const totalReceitas = receitas?.reduce((sum, r) => sum + Number(r.valor), 0) || 0;
      const totalDespesas = despesas?.reduce((sum, d) => sum + Number(d.valor), 0) || 0;
      const profitMargin = totalReceitas > 0 
        ? Math.min(100, Math.max(0, ((totalReceitas - totalDespesas) / totalReceitas) * 100))
        : 50;

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const { data: tasks } = await supabase
        .from('project_tasks')
        .select('status, created_at')
        .gte('created_at', thirtyDaysAgo.toISOString());

      const totalTasks = tasks?.length || 0;
      const completedTasks = tasks?.filter(t => t.status === 'concluída').length || 0;
      const taskVelocity = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 50;

      const { data: propostas } = await supabase
        .from('propostas')
        .select('status')
        .neq('status', 'rascunho');

      const totalPropostas = propostas?.length || 0;
      const aprovadasCount = propostas?.filter((p: any) => p.status === 'aprovada').length || 0;
      const proposalConversion = totalPropostas > 0 ? (aprovadasCount / totalPropostas) * 100 : 50;

      const { data: projects } = await supabase
        .from('projects')
        .select('progress, status')
        .in('status', ['em_andamento', 'planejamento']);

      const avgProgress = projects && projects.length > 0
        ? projects.reduce((sum, p) => sum + (p.progress || 0), 0) / projects.length
        : 50;

      const finalScore = Math.round(
        (profitMargin * 0.30) +
        (taskVelocity * 0.25) +
        (proposalConversion * 0.25) +
        (avgProgress * 0.20)
      );

      setMetrics({
        profitMargin: Math.round(profitMargin),
        taskVelocity: Math.round(taskVelocity),
        proposalConversion: Math.round(proposalConversion),
        projectProgress: Math.round(avgProgress)
      });
      
      setScore(Math.min(100, Math.max(0, finalScore)));
    } catch (error) {
      console.error('Error calculating health score:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAIDiagnosis = async () => {
    if (!user?.id) return;
    
    try {
      setDiagnosisLoading(true);
      
      const { data, error } = await supabase.functions.invoke('ai-office-diagnosis', {
        body: { metrics, score }
      });

      if (error) throw error;
      setDiagnosis(data);
    } catch (error) {
      console.error('Error fetching AI diagnosis:', error);
      if (score >= 70) {
        setDiagnosis({
          diagnosis: 'Seu escritório está operando bem. Margem saudável e boa produtividade.',
          action: 'Foque em expandir a captação de novos projetos mantendo a qualidade.'
        });
      } else if (score >= 50) {
        setDiagnosis({
          diagnosis: 'Existem pontos de melhoria. Analise os indicadores abaixo do esperado.',
          action: 'Priorize a conclusão de tarefas atrasadas e acompanhe propostas pendentes.'
        });
      } else {
        setDiagnosis({
          diagnosis: 'Atenção necessária. Indicadores mostram gargalos operacionais.',
          action: 'Revise custos, renegocie prazos e foque em fechar propostas abertas.'
        });
      }
    } finally {
      setDiagnosisLoading(false);
    }
  };

  useEffect(() => {
    calculateHealthScore();
  }, [user?.id]);

  useEffect(() => {
    if (!loading && score > 0) {
      fetchAIDiagnosis();
    }
  }, [loading, score]);

  const { grade, color, label } = getScoreGrade(score);

  const formatCurrency = (value: number) => {
    if (value >= 1000) return `R$ ${(value / 1000).toFixed(1)}k`;
    return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 0 })}`;
  };

  const miniStats = [
    { 
      icon: DollarSign, 
      label: 'Receita', 
      value: formatCurrency(monthlyRevenue),
      color: 'text-emerald-500'
    },
    { 
      icon: Building2, 
      label: 'Projetos', 
      value: `${projectStats.total - projectStats.completed}`,
      color: 'text-blue-500'
    },
    { 
      icon: CheckCircle, 
      label: 'Conclusão', 
      value: `${completionRate}%`,
      color: 'text-primary'
    },
    { 
      icon: AlertTriangle, 
      label: 'Alertas', 
      value: alertsCount.toString(),
      color: alertsCount > 0 ? 'text-amber-500' : 'text-muted-foreground'
    },
  ];

  return (
    <Card className="bento-card overflow-hidden border-0 bg-gradient-to-br from-card via-card to-muted/20">
      <div className="p-6 lg:p-8">
        <div className="flex flex-col lg:flex-row gap-8 items-center lg:items-start">
          {/* Score Circle */}
          <div className="relative flex-shrink-0">
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', bounce: 0.3, duration: 0.8 }}
            >
              <svg className="w-36 h-36 lg:w-44 lg:h-44 transform -rotate-90">
                <circle
                  cx="50%"
                  cy="50%"
                  r="45%"
                  stroke="currentColor"
                  strokeWidth="10"
                  fill="transparent"
                  className="text-muted/20"
                />
                <motion.circle
                  cx="50%"
                  cy="50%"
                  r="45%"
                  strokeWidth="10"
                  fill="transparent"
                  strokeLinecap="round"
                  className={getScoreRingColor(score)}
                  initial={{ strokeDasharray: '0 283' }}
                  animate={{ strokeDasharray: `${(score / 100) * 283} 283` }}
                  transition={{ duration: 1.2, ease: 'easeOut', delay: 0.3 }}
                />
              </svg>
              
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <motion.span
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className={`text-5xl lg:text-6xl font-bold ${color}`}
                >
                  {loading ? '--' : score}
                </motion.span>
                <span className="text-xs text-muted-foreground mt-1">de 100</span>
              </div>
            </motion.div>

            {/* Grade Badge */}
            <motion.div 
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.8, type: 'spring' }}
              className="absolute -top-2 -right-2"
            >
              <Badge className={`${color} bg-background border-2 border-current font-bold text-xl px-3 py-1 shadow-lg`}>
                {grade}
              </Badge>
            </motion.div>
          </div>

          {/* Diagnosis Content */}
          <div className="flex-1 text-center lg:text-left space-y-4">
            <div>
              <div className="flex items-center justify-center lg:justify-start gap-2 mb-2">
                <Activity className="h-5 w-5 text-primary" />
                <h2 className="text-xl font-bold text-foreground">Saúde do Escritório</h2>
                <Badge variant="secondary" className="ml-2 gap-1">
                  {score >= 55 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  {label}
                </Badge>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 ml-auto"
                  onClick={calculateHealthScore}
                  disabled={loading}
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>

            {/* AI Diagnosis */}
            {diagnosis && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="p-4 rounded-xl bg-primary/5 border border-primary/10"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium text-primary">Diagnóstico IA</span>
                  {diagnosisLoading && <RefreshCw className="h-3 w-3 animate-spin" />}
                </div>
                <p className="text-sm text-foreground mb-3">{diagnosis.diagnosis}</p>
                <div className="flex items-start gap-2 pt-3 border-t border-primary/10">
                  <Brain className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">Ação:</span> {diagnosis.action}
                  </p>
                </div>
              </motion.div>
            )}
          </div>
        </div>

        {/* Mini Stats Bar */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8 pt-6 border-t border-border/50"
        >
          {miniStats.map((stat, index) => (
            <div key={stat.label} className="flex items-center gap-3">
              <div className={`p-2 rounded-lg bg-muted/50 ${stat.color}`}>
                <stat.icon className="h-4 w-4" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
                <p className="text-lg font-bold text-foreground">{stat.value}</p>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </Card>
  );
};
